/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState } from "react";
import "../MissionDetails.css";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { DEFAULT_LANGUAGE_ARRAY } from "../../../config";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import errorMsg from "../../../assets/json/errMsg.json";
import {
  MIN_RECURRING_OCCURRENCES,
  MAX_RECURRING_OCCURRENCES,
} from "../../../config";
import { NumericFormat } from "react-number-format";
import { getTextColor, getContrastTextColor, resolveThemeColors, getEffectiveFeeCoverageMode } from "../../../assets/helper.js";
import { getProjectTitle } from "../../../assets/projectTitleHelper.js";
import RenderHeader from "../../../assets/component/RenderProjectHeader.js";
import { QRCodeCanvas } from "qrcode.react";
import { Editor } from "draft-js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getProjectDescription } from "../../../assets/projectContentHelper.js";
import { validPaymentInfo } from "../../../assets/validators/payment.js";
import moment from "moment";
import { renderSelectML } from "../../../assets/component/renderSelectML.js";

const ViewMission = (props) => {
  if (!props.func) {
    return;
  }

  /* Use this hook for Read More/Less button */
  const [active, setActive] = useState(false);
  /* Use this hook for custom amount input visibility */
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  
  const amount = props.func.state.amount;

  const progressStyle = {
    background: "#" + props.func.state.organization.progressBarColor,
  };

  const progressBgStyle = {
    background: "#" + props.func.state.organization.progressBarColor,
    opacity: "0.3",
  };

  const currentAmount = props.func.state.mission && props.func.state.mission.currentAmount 
    ? parseFloat(props.func.state.mission.currentAmount).toFixed(2)
    : "0.00";
  const targetAmount = props.func.state.mission && props.func.state.mission.targetAmount 
    ? props.func.state.mission.targetAmount 
    : 0;
  if (targetAmount === 0.0) {
    progressStyle.width = "100%";
  } else {
    let percentage = currentAmount / targetAmount;
    percentage *= 100.0;
    progressStyle.width = String(percentage) + "%";
  }

  props.func.onScroll = () => {
    if (window.scrollY >= 100) {
      props.func.setState({ isScrolling: true });
    } else {
      props.func.setState({ isScrolling: false });
    }
  };


  const logoStyle = props.func.state.organization.logo;

  const organizationName = props.func.state.organization.organizationName;

  const resolved = resolveThemeColors(
    props.func.state.organization.headerColor,
    props.func.state.organization.buttonColor
  );

  const color = {
    headerColor: resolved.headerColor,
    buttonColor: resolved.buttonColor,
  };

  const languageObj = props.func.props.languageObj;

  const effectiveFeeMode = getEffectiveFeeCoverageMode(
    props.func.state.organization?.feeCoverageMode,
    props.func.state.mission?.feeCoverageMode,
  );
  const donorCanChooseFee = effectiveFeeMode === 'optional';

  const renderFeeEstimate = (variant) => {
    const est = props.func.state.feeCoverageEstimate;
    const estAmex = props.func.state.feeCoverageEstimateAmex;
    const loading = props.func.state.feeCoverageEstimateLoading;
    // Org toggle gates the optional case; when the project forces fees on,
    // donors see the estimate regardless of the org toggle.
    if (!props.func.state.organization?.showFeeCoverageAmount && effectiveFeeMode !== 'required') return null;
    if (!props.func.state.coverFee) return null;
    if (!est && !loading) return null;
    const wrapperClass = variant === 'inline' ? 'MissionDetails-feeEstimateInline' : 'MissionDetails-feeEstimate';
    if (loading) {
      return (
        <div className={wrapperClass} aria-busy="true" aria-live="polite">
          <div className="MissionDetails-feeEstimateRow">
            <span>{languageObj.estimatedFee || "Estimated fee"}</span>
            <span className="MissionDetails-feeEstimateSkeleton MissionDetails-feeEstimateSkeleton--short" />
          </div>
          <div className="MissionDetails-feeEstimateRow MissionDetails-feeEstimateRow--total">
            <span>{languageObj.totalCharged || "Total charged"}</span>
            <span className="MissionDetails-feeEstimateSkeleton MissionDetails-feeEstimateSkeleton--long" />
          </div>
          <div className="MissionDetails-feeEstimateNote">{languageObj.calculatingFee || "Calculating fee…"}</div>
        </div>
      );
    }
    // Span every method we priced (card brands plus PayMe when the donor can
    // reach it) so the range reflects what they could actually pay (CP-281).
    const priced = [est, estAmex, props.func.state.feeCoverageEstimatePayMe].filter(
      (e) => e && e.totalFee != null && e.totalAmount != null
    );
    if (priced.length === 0) return null;
    const money = (v) => `HK$${Number(v).toFixed(2)}`;
    const fees = priced.map((e) => Number(e.totalFee));
    const totals = priced.map((e) => Number(e.totalAmount));
    const minFee = Math.min(...fees);
    const maxFee = Math.max(...fees);
    const minTotal = Math.min(...totals);
    const maxTotal = Math.max(...totals);
    const includesPayMe = !!props.func.state.feeCoverageEstimatePayMe;
    const showRange = minFee !== maxFee;
    const showTotalRange = minTotal !== maxTotal;
    return (
      <div className={wrapperClass}>
        <div className="MissionDetails-feeEstimateRow">
          <span>{languageObj.estimatedFee || "Estimated fee"}</span>
          <span>{showRange ? `${money(minFee)} – ${money(maxFee)}` : money(minFee)}</span>
        </div>
        <div className="MissionDetails-feeEstimateRow MissionDetails-feeEstimateRow--total">
          <span>{languageObj.totalCharged || "Total charged"}</span>
          <span>{showTotalRange ? `${money(minTotal)} – ${money(maxTotal)}` : money(minTotal)}</span>
        </div>
        <div className="MissionDetails-feeEstimateNote">
          {/* Once PayMe is in the span the range crosses payment methods, not
              just card brands, so the card-brand wording no longer fits. */}
          {includesPayMe
            ? (languageObj.feeVariesByMethodNote || "Final fee depends on your payment method")
            : showRange
              ? (languageObj.feeCreditCardRangeNote || "Visa/Mastercard – Amex")
              : (languageObj.feeCreditCardNote || "Credit card payments only")}
        </div>
      </div>
    );
  };

  const originalHeaderOverrideStyle = {
    background: "#" + props.func.state.organization.headerColor,
  };

  const headerOverrideStyle = {
    background: "#" + resolved.headerColor,
  };

  // console.log(JSON.stringify(headerOverrideStyle) + " -88");

  const buttonOverrideStyle = {
    background: "#" + resolved.buttonColor,
  };


  const buttonOverrideTextStyle = {
    color: getTextColor(resolved.buttonColor),
  };



  // Check if amount is valid
  const minimumPaymentAmount = props.func.state.organization.minimumPaymentAmount || 0;
  // console.log("ViewMission: amount =", props.func.state.amount, "minimumPaymentAmount =", minimumPaymentAmount, "validData =", props.func.state.validData);
  const isAmountValid = props.func.state.amount >= minimumPaymentAmount || props.func.state.validData;

  // Check if recurring settings are valid
  const isRecurringFrequency = props.func.state.frequency === "WEEKLY" ||
                                props.func.state.frequency === "MONTHLY";
  const isRecurringValid = !isRecurringFrequency ||
    props.func.state.isInfiniteRecurring ||
    (props.func.state.recurringOccurrences > 0 && props.func.state.recurringOccurrences < 25);

  // When fees are mandatory, hold proceed until the estimate has loaded so the
  // donor sees what they're being charged before continuing.
  const isFeeEstimateLoading = effectiveFeeMode === 'required'
    && props.func.state.amount > 0
    && !props.func.state.feeCoverageEstimate;

  // Button is enabled if both amount and recurring settings are valid, and we're
  // not still loading a mandatory fee estimate.
  const isNextBtnEnabled = isAmountValid && isRecurringValid && !isFeeEstimateLoading;

  const nextBtnStyle = {
    background: "#" + resolved.headerColor,
    color: getContrastTextColor(resolved.headerColor, resolved.buttonColor),
    cursor: isNextBtnEnabled ? "pointer" : "not-allowed",
    pointerEvents: isNextBtnEnabled ? "auto" : "none",
    filter: isNextBtnEnabled ? "brightness(100%)" : "brightness(80%)",
  };

  const frameDotStyle = {
    background: "#" + props.func.state.organization.progressBarColor,
  };

  /* Styling For Choosing Frequencies */
  const frequencyNotChosen = {
    backgroundColor: "#F4F4F5",
    color: "rgba(0, 0, 0, 0.3)",
  };

  const frequencyChosen = {
    background: "#" + resolved.headerColor,
    color: getContrastTextColor(resolved.headerColor, resolved.buttonColor),
  };

  /* SECTION ENDS HERE */

  //const buttonBgStyle = {
  //background: "#" + resolved.buttonColor,
  //};

  const currentPlan = props.func.state.organization.currentPlan ? props.func.state.organization.currentPlan.toLowerCase() : '';



  const headerImg = props.func.state.mission.headerBackgroundPicture;

  const qrUrl = window.location.href;

  const amountBool = props.func.state.mission.targetAmount > 0;

  const RenderDescription = () => {
    const description = props.func.state.mission.description;
    // const test = description.default.getCurrentContent().getFirstBlock().getText()
    // console.log(test.split('\n'))
    try {
      let lngArr = DEFAULT_LANGUAGE_ARRAY;
      lngArr.push("default");
      const descriptionData = getProjectDescription(
        props.func.props.language,
        description,
        props.func.props.lngArr,
        "string",
        true
      );
      return (
        <div
          className={`MissionDetails-descriptionWrapper ${active ? "expanded" : "collapsed"}`}
        >
          <Editor
            editorState={descriptionData}
            readOnly={true}
            className="MissionDetails-description"
          />
        </div>
      );
    } catch (e) {
      const htmlString = description.split("\n");
      return (
        <div className={`MissionDetails-descriptionWrapper ${active ? "expanded" : "collapsed"}`}>
          <div className="MissionDetails-description">
            {htmlString.map((item, i) => {
              return <p key={i}>{item}</p>;
            })}
          </div>
        </div>
      );
    }
  };

  const RenderTopBar = () => {
    const organization = props.func.state.organization || {};
    const supportedLanguages = organization.supportedLanguages || [];
    const currentPlan = organization.currentPlan || '';
    const isPremiumOrPro = currentPlan === 'premium' || currentPlan === 'pro';

    // console.log('MissionDetails RenderTopBar Debug:', {
    //   organization,
    //  supportedLanguages,
    //  supportedLanguagesLength: supportedLanguages.length,
    //  currentPlan,
    //  isPremiumOrPro,
    //  shouldShow: supportedLanguages.length > 1 && isPremiumOrPro
    // });

    // Only show language selector if multiple languages are supported AND user is on Pro/Premium plan
    if (supportedLanguages.length <= 1 || !isPremiumOrPro) {
      return null;
    }

    return (
      <div
        className="MissionDetails-languageSelector"
        style={buttonOverrideStyle}
      >
        <div className="MissionDetails-globe" style={headerOverrideStyle}></div>
        {renderSelectML(buttonOverrideStyle, buttonOverrideTextStyle, supportedLanguages)}
      </div>
    );
  };

  const RenderLogin = () => {
    if (props.func.props.isAuth) {
      return (
        <div className="MissionDetails-RenderLogin">
          <Link
            className="MissionDetails-loginBtnContainer"
            style={buttonOverrideStyle}
            to={{
              pathname: "/AcInfo",
              state: {
                logoStyle: logoStyle,
                color: color,
                organizationName: organizationName,
              },
            }}
          >
            <div
              className="MissionDetails-userIcn"
              style={headerOverrideStyle}
            ></div>
          </Link>

          <Link
            to={{
              pathname: "/Transactions",
              state: {
                ...props.func.state,
              },
            }}
            className="MissionDetails-recordContainer"
            style={buttonOverrideStyle}
          >
            <div
              className="MissionDetails-recordIcon"
              style={headerOverrideStyle}
            ></div>
          </Link>

          <Link
            className="MissionDetails-recordContainer"
            style={buttonOverrideStyle}
            to={{
              pathname: "/ListCards",
              state: {
                ...props.func.state,
                ...{
                  color: color,
                  organizationName: organizationName,
                  subdomain: props.func.state.subdomain,
                  domain: props.func.state.domain,
                  isCustomDomain: props.func.state.isCustomDomain,
                },
              },
            }}
          >
            <div
              className="MissionDetails-cardIcn"
              style={headerOverrideStyle}
            ></div>
          </Link>
        </div>
      );
    }

    return (
      <Link
        className="MissionDetails-loginBtnContainer"
        style={buttonOverrideStyle}
        to={{
          pathname: "/LoginPrompt",
          state: {
            logoStyle,
          },
        }}
      >
        <div
          className="MissionDetails-userIcn"
          style={headerOverrideStyle}
        ></div>
      </Link>
    );
  };

  const errRender = () => {
    const minimum = props.func.state.organization.minimumPaymentAmount;

    if (props.func.state.amount >= minimum || props.func.state.validData) {
      return null;
    }
    const err =
      errorMsg[props.func.props.language].paymentErr.minimum + minimum;

    return <div>{renderErrorMsg(err)}</div>;
  };

  const handleAmountChange = (e) => {
    let amount = e.target.value;

    let amountSplit = amount.split("$");
    if (!amountSplit[1]) return false;
    amountSplit = amountSplit[1].split(",");

    let a = "";
    for (var i = 0; i < amountSplit.length; i++) {
      a += amountSplit[i];
    }

    const isAmount = validPaymentInfo.validAmount(
      a,
      props.func.state.organization.minimumPaymentAmount
    );
    const numberAmount = Number(a);

    props.func.setState({
      amount: numberAmount,
      validData: isAmount,
    });
  };

  const handleDateChange = (date) => {
    const selectedDate = moment(date).startOf('day');
    const tomorrow = moment().add(1, 'days').startOf('day');
  
    // If the selected date is before tomorrow, set it to tomorrow
    const finalDate = selectedDate.isBefore(tomorrow) ? tomorrow : selectedDate;
  
    props.func.setState({
      ...props.func.state,
      ...{
        scheduledDate: finalDate.hour(8).minute(0).second(0).millisecond(0).toISOString(false),
        startDate: finalDate.toDate(),
      },
    });
  };

  const _handleOccurrences = (e) => {
    let errMsg = "";
    if (!validPaymentInfo.validOccurrences(e.target.value))
      errMsg = "Frequency is required";

    props.func.setState({
      recurringOccurrences: e.target.value,
      roErrMsg: errMsg,
    });
  };

  // Initialize text variables with default empty strings
  let defaultText = "";
  let zhText = "";
  let cnText = "";
  let enText = "";

  try {
    // Only try to access description properties if they exist
    if (props.func.state.mission.description) {
      if (props.func.state.mission.description.default) {
        defaultText = props.func.state.mission.description.default
          .getCurrentContent()
          .getFirstBlock()
          .getText();
      }

      if (props.func.state.mission.description.zh) {
        zhText = props.func.state.mission.description.zh
          .getCurrentContent()
          .getFirstBlock()
          .getText();
      }

      if (props.func.state.mission.description.cn) {
        cnText = props.func.state.mission.description.cn
          .getCurrentContent()
          .getFirstBlock()
          .getText();
      }

      if (props.func.state.mission.description.en) {
        enText = props.func.state.mission.description.en
          .getCurrentContent()
          .getFirstBlock()
          .getText();
      }
    }
  } catch (e) {
    // console.error("Error processing description text:", e);
    // Keep default empty strings if any error occurs
  }

  // Safely calculate plainText with fallbacks
  const plainText =
    (enText ? enText.length : 0) + 
    (cnText ? cnText.length : 0) + 
    (zhText ? zhText.length : 0) + 
    (defaultText ? defaultText.length : 0);

  const minDate = new Date(Date.now() + 3600 * 1000 * 24);
  return (
    <div className="MissionDetails-view" style={originalHeaderOverrideStyle}>
      <div className="MissionDetails-header" style={originalHeaderOverrideStyle}>
        <div
          className="MissionDetails-logo"
          style={props.func.state.organization.logo}
        >
          {!props.func.state.organization.logo ? "L O G O" : ""}
        </div>

        <div className="MissionDetails-mainToolBarContainer">
          <div className="MissionDetails-bg" style={originalHeaderOverrideStyle} />
          <RenderLogin />

          {(props.func.state.organization.currentPlan === "premium" || props.func.state.organization.currentPlan === "pro") && (<RenderTopBar />)}
        </div>

        <div className="MissionDetails-QR">
          <div className="MissionDetails-centerQR">
            <QRCodeCanvas
              style={{
                height: "100%",
                width: "100%",
              }}
              value={qrUrl}
            />
          </div>
        </div>
      </div>

      <div className="MissionDetails-stepsContainer">
        {props.func.state.mission.status === "open" && (
          <div
            className="MissionDetails-backContainer"
            onClick={() => props.func.props.history.push(props.backpath)} // Correctly use props.backpath
          >
            <div className="MissionDetails-back">
              <div className="MissionDetails-backBtn" />
              <div className="MissionDetails-backText">
                {languageObj.backText}
              </div>
            </div>
          </div>
        )}

        {props.func.state.isScrolling ? (
          <div
            className="MissionDetails-smallLogoContainer MissionDetails-fadeIn"
            style={headerOverrideStyle}
          >
            <div
              className="MissionDetails-logoS"
              style={props.func.state.organization.logo}
            ></div>
          </div>
        ) : (
          <div></div>
        )}
        <div className="MissionDetails-stepTxt">
          {props.func.state.mission.status === "unlisted" 
            ? "Step 1 of 4" 
            : languageObj.stepTxt}
        </div>
        <div className="MissionDetails-indicator">
          {props.func.state.mission.status === "unlisted" ? (
            // 4 steps for unlisted projects, with first one highlighted
            <>
              <div className="MissionDetails-circle MissionDetails-circleSelected MissionDetails-marginRight" />
              <div className="MissionDetails-circle MissionDetails-circleEmpty MissionDetails-marginRight" />
              <div className="MissionDetails-circle MissionDetails-circleEmpty MissionDetails-marginRight" />
              <div className="MissionDetails-circle MissionDetails-circleEmpty" />
            </>
          ) : (
            // 5 steps for open projects, with first two highlighted (original behavior)
            <>
              <div className="MissionDetails-circle MissionDetails-circleSelected MissionDetails-marginRight" />
              <div className="MissionDetails-circle MissionDetails-circleSelected MissionDetails-marginRight" />
              <div className="MissionDetails-circle MissionDetails-circleEmpty MissionDetails-marginRight" />
              <div className="MissionDetails-circle MissionDetails-circleEmpty MissionDetails-marginRight" />
              <div className="MissionDetails-circle MissionDetails-circleEmpty" />
            </>
          )}
        </div>
      </div>
      {props.func.state.mission ? (
        <>
          {amountBool ? (
            <div className="MissionDetails-goalSectionWrapper">
              <div className="MissionDetails-goalSection">
                <div className="MissionDetails-goalContainer">
                  {renderBar(
                    targetAmount,
                    currentAmount,
                    progressStyle,
                    progressBgStyle
                  )}
                  <div className="MissionDetails-goal">
                    <div className="MissionDetails-raisedContainer">
                      <div
                        className="MissionDetails-amountDot"
                        style={frameDotStyle}
                      />
                      <div className="MissionDetails-amountContainer">
                        <NumericFormat
                          decimalSeparator="."
                          displayType="text"
                          className="MissionDetails-amountNumber"
                          valueIsNumericString={true}
                          value={currentAmount}
                          allowNegative={false}
                          thousandSeparator={true}
                          prefix={"HK$"}
                          fixedDecimalScale={true}
                          decimalScale={2}
                        />
                        <div className="MissionDetails-amountRaised">
                          {languageObj.amountRaised}
                        </div>
                      </div>
                    </div>

                    <div className="MissionDetails-alreadyRaisedContainer">
                      <div
                        className="MissionDetails-raisedDot"
                        style={progressBgStyle}
                      />
                      <div className="MissionDetails-amountContainer">
                        <NumericFormat
                          decimalSeparator="."
                          displayType="text"
                          className="MissionDetails-raisedNumber"
                          valueIsNumericString={true}
                          value={targetAmount}
                          allowNegative={false}
                          thousandSeparator={true}
                          prefix={"HK$"}
                          fixedDecimalScale={true}
                          decimalScale={2}
                        />
                        <div className="MissionDetails-goalRaised">
                          {languageObj.goal}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="MissionDetails-loadingNextBtn MissionDetails-gradient">
          Loading...
        </div>
      )}
      <div className="MissionDetails-pictureandBodyContainer">
        <div
          className={
            headerImg !== null
              ? "MissionDetails-pictureandBody"
              : "MissionDetails-bodyNoPicture"
          }
        >
          {headerImg !== null ? (
            <div className="MissionDetails-pictureColumn">
              <div className="MissionDetails-picture">
                <RenderHeader headerBackgroundPicture={headerImg} />
              </div>
              {/* Project Name and Description */}
              <div className="MissionDetails-projectInfo">
                <div className="MissionDetails-projectTitle">
                  {getProjectTitle(props.func.props.language, props.func.state.mission)}
                </div>
                {plainText > 0 ? (
                  <div>
                    <div className="MissionDetails-projectDescription">
                      <RenderDescription />
                    </div>
                    <button
                      className="MissionDetails-descriptionBtn"
                      onClick={() => active ? setActive(false) : setActive(true)}
                    >
                      <span>{!active ? languageObj.readmore : languageObj.readless}</span>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <div
            className={
              headerImg !== null
                ? "MissionDetails-inputArea"
                : "MissionDetails-inputAreaNoPicture"
            }
          >
            <div className="MissionDetails-inputAmount">
              {/* Payment Frequency */}
              {(props.func.state.organization.currentPlan === "premium" || props.func.state.organization.currentPlan === "pro") && (
                <>
                  <div className="MissionDetails-recurringSection">
                    <div className="MissionDetails-sectionHeading">
                      {languageObj.transactionFrequency || "Transaction Frequency"}
                    </div>
                    <div className="MissionDetails-frequencyContainer" role="radiogroup" aria-label="Payment frequency">
                      <div
                        className="MissionDetails-frequencyOption"
                        data-testid="missiondetails-frequency-onetime"
                        style={{
                          ...(props.func.state.frequency === "ONETIME"
                            ? frequencyChosen
                            : frequencyNotChosen),
                        }}
                        onClick={() => {
                          if (props.func.state.frequency !== "ONETIME") {
                            props.func.setState({
                              frequency: "ONETIME",
                            });
                          }
                        }}
                      >
                        <b>{languageObj.onetime}</b>
                      </div>
                      <div
                        className="MissionDetails-frequencyOption"
                        data-testid="missiondetails-frequency-recurring"
                        style={{
                          ...(props.func.state.frequency !== "ONETIME"
                            ? frequencyChosen
                            : frequencyNotChosen),
                        }}
                        onClick={() => {
                          if (props.func.state.frequency === "ONETIME") {
                            // When switching to recurring, set scheduledDate to startDate (tomorrow)
                            const tomorrow = moment().add(1, 'days').startOf('day');
                            // Preserve validData if amount is already valid
                            const currentValidData = props.func.state.validData;
                            const isCurrentAmountValid = props.func.state.amount >= minimumPaymentAmount;
                            props.func.setState({
                              frequency: "MONTHLY",
                              isInfiniteRecurring: true,
                              recurringOccurrences: 0,
                              scheduledDate: tomorrow.hour(8).minute(0).second(0).millisecond(0).toISOString(false),
                              startDate: tomorrow.toDate(),
                              validData: currentValidData !== undefined ? currentValidData : isCurrentAmountValid
                            });
                          }
                        }}
                      >
                        <b>{languageObj.recurring || "Recurring"}</b>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Amount Selection */}
              <div className="MissionDetails-amountSection">
                {props.func.state.frequency !== "ONETIME" && props.func.state.amount > 0 ? (
                  <>
                    <div className="MissionDetails-sectionHeading">
                      {languageObj.selectedAmount || "Selected Amount"}
                    </div>
                    <div className="MissionDetails-amountMinimizedCard">
                      <div className="MissionDetails-amountMinimized">
                        <div className="MissionDetails-minimizedAmount">
                          HK${props.func.state.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          <span className="MissionDetails-frequencyBadge" style={{ background: "#" + resolved.headerColor, color: getContrastTextColor(resolved.headerColor, resolved.buttonColor) }}>
                            {props.func.state.frequency === "MONTHLY" ? (languageObj.perMonth || "/month") : (languageObj.perWeek || "/week")}
                          </span>
                        </div>
                        <button type="button" className="MissionDetails-changeAmountBtn" onClick={() => { props.func.setState({ amount: 0, validData: false }); setShowCustomAmount(false); }}>
                          {languageObj.changeAmount || "Change"}
                        </button>
                      </div>
                      {donorCanChooseFee && (
                        <div className="MissionDetails-coverFeeInline">
                          <label className="MissionDetails-coverFeeInlineLabel" htmlFor="cover-fee-checkbox-minimized">
                            <input id="cover-fee-checkbox-minimized" type="checkbox" checked={props.func.state.coverFee} onChange={props.func.handleCoverFeeToggle} className="MissionDetails-checkbox" style={{ accentColor: "#" + resolved.buttonColor }} aria-describedby="cover-fee-description-minimized" />
                            <span className="MissionDetails-checkboxText" id="cover-fee-description-minimized">
                              {languageObj.coverFee || "I'll cover the transaction fees"}
                            </span>
                          </label>
                        </div>
                      )}
                      {renderFeeEstimate('inline')}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="MissionDetails-sectionHeading">
                      {languageObj.chooseAmount || "Choose Amount"}
                    </div>
                    <div className="MissionDetails-amountShortcuts">
                      {(Array.isArray(props.func.state.organization.presetDonationAmounts) &&
                        props.func.state.organization.presetDonationAmounts.length > 0
                        ? props.func.state.organization.presetDonationAmounts
                        : [100, 250, 500, 1000]
                      ).map((shortcutAmount) => (
                        <button
                          key={shortcutAmount}
                          type="button"
                          className="MissionDetails-amountShortcutBtn"
                          data-testid={`missiondetails-amount-shortcut-${shortcutAmount}`}
                          style={{
                            background: props.func.state.amount === shortcutAmount && !showCustomAmount
                              ? "#" + resolved.headerColor
                              : "transparent",
                            color: props.func.state.amount === shortcutAmount && !showCustomAmount
                              ? "#" + resolved.buttonColor
                              : "#0f172a",
                            border: props.func.state.amount === shortcutAmount && !showCustomAmount
                              ? "2px solid #" + resolved.headerColor
                              : "2px solid rgba(0, 0, 0, 0.1)",
                          }}
                          onClick={() => {
                            props.func.setState({
                              amount: shortcutAmount,
                              validData: true,
                            });
                            setShowCustomAmount(false);
                          }}
                        >
                          HK${shortcutAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="MissionDetails-amountShortcutBtn MissionDetails-otherAmountBtn"
                        style={{
                          background: showCustomAmount
                            ? "#" + resolved.headerColor
                            : "transparent",
                          color: showCustomAmount
                            ? "#" + resolved.buttonColor
                            : "#0f172a",
                          border: showCustomAmount
                            ? "2px solid #" + resolved.headerColor
                            : "2px solid rgba(0, 0, 0, 0.1)",
                        }}
                        onClick={() => {
                          setShowCustomAmount(true);
                          props.func.setState({
                            amount: 0,
                            validData: false,
                          });
                        }}
                      >
                        {languageObj.otherAmount || "Other Amount"}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Custom Amount Input - Shows when "Other Amount" is selected */}
              {showCustomAmount && (
                <div className="MissionDetails-customAmountContainer">
                  <div className="MissionDetails-inputContainer" role="group" aria-label="Enter donation amount in HKD">
                    <div className="MissionDetails-currencySign">HKD</div>
                    <NumericFormat
                      pattern="[0-9]*"
                      inputMode="decimal"
                      decimalSeparator="."
                      className="MissionDetails-dollarField"
                      style={{ bottom: "4px" }}
                      valueIsNumericString={true}
                      value={amount}
                      onChange={handleAmountChange}
                      allowNegative={false}
                      thousandSeparator={true}
                      prefix={"$"}
                      fixedDecimalScale={true}
                      decimalScale={2}
                      autoFocus
                    />
                  </div>
                  <div className="MissionDetails-errorContainer">{errRender()}</div>
                </div>
              )}

              {/* Cover Fee Checkbox - Only show for one-time when donor can choose */}
              {props.func.state.amount > 0 && props.func.state.frequency === "ONETIME" && donorCanChooseFee && (
                <div className="MissionDetails-coverFeeSection" role="region" aria-label="Transaction fee options">
                  <label className="MissionDetails-checkboxLabel">
                    <input
                      type="checkbox"
                      checked={props.func.state.coverFee}
                      onChange={props.func.handleCoverFeeToggle}
                      className="MissionDetails-checkbox"
                      style={{ accentColor: "#" + resolved.buttonColor }}
                      aria-describedby="cover-fee-description"
                    />
                    <span className="MissionDetails-checkboxText" id="cover-fee-description">
                      {languageObj.coverFee || "I'll cover the transaction fees"}
                    </span>
                  </label>
                  {renderFeeEstimate('block')}
                </div>
              )}

              {/* Fee estimate for ONETIME when mode is 'required' (checkbox hidden, fee forced on) */}
              {props.func.state.amount > 0 && props.func.state.frequency === "ONETIME" && effectiveFeeMode === 'required' && renderFeeEstimate('block')}

              {/* Payment Schedule - Combined card with all recurring settings */}
              {(props.func.state.organization.currentPlan === "premium" || props.func.state.organization.currentPlan === "pro") && props.func.state.frequency !== "ONETIME" && props.func.state.amount > 0 && (
                <div className="MissionDetails-recurringSection" data-testid="missiondetails-recurring-schedule">
                  <div className="MissionDetails-sectionHeading">
                    {languageObj.paymentSchedule || "Payment Schedule"}
                  </div>
                  <div className="MissionDetails-scheduleCard">
                    {/* Frequency Dropdown */}
                    <div className="MissionDetails-scheduleRow">
                      <div className="MissionDetails-scheduleLabel">
                        {languageObj.transactionFrequency || "Transaction Frequency"}
                      </div>
                      <select
                        className="MissionDetails-frequencyDropdown"
                        value={props.func.state.frequency}
                        onChange={(e) => {
                          props.func.setState({
                            frequency: e.target.value,
                            isInfiniteRecurring: true,
                            recurringOccurrences: 0
                          });
                        }}
                      >
                        <option value="MONTHLY">{languageObj.monthly || "Monthly"}</option>
                        <option value="WEEKLY">{languageObj.weekly || "Weekly"}</option>
                      </select>
                    </div>
                    {/* Date Picker */}
                    <div className="MissionDetails-scheduleRow">
                      <div className="MissionDetails-scheduleLabel">
                        {languageObj.startDate || "Start Date"}
                      </div>
                      <div className="MissionDetails-inputContainer">
                        <DatePicker
                          className="MissionDetails-dateInput"
                          selected={props.func.state.startDate || minDate}
                          minDate={minDate}
                          onChange={handleDateChange}
                          dateFormat="yyyy/MM/dd"
                        />
                      </div>

                      {/* Day of week selector for Weekly */}
                      {props.func.state.frequency === "WEEKLY" && (
                        <div className="MissionDetails-scheduleRow" style={{ marginTop: '12px' }}>
                          <div className="MissionDetails-scheduleLabel">
                            {languageObj.dayOfWeek || "Day of week"}
                          </div>
                          <select
                            className="MissionDetails-frequencyDropdown"
                            value={(() => {
                              return props.func.state.startDate
                                ? new Date(props.func.state.startDate).getDay()
                                : new Date().getDay();
                            })()}
                            onChange={(e) => {
                              const targetDay = parseInt(e.target.value);
                              const currentDate = props.func.state.startDate
                                ? new Date(props.func.state.startDate)
                                : new Date();
                              const currentDay = currentDate.getDay();
                              const dayDiff = targetDay - currentDay;

                              const newDate = new Date(currentDate);
                              newDate.setDate(newDate.getDate() + dayDiff);

                              // Ensure the new date is not before tomorrow
                              const tomorrow = moment().add(1, 'days').startOf('day');
                              const selectedDate = moment(newDate).startOf('day');
                              const finalDate = selectedDate.isBefore(tomorrow)
                                ? tomorrow.add((7 + targetDay - tomorrow.day()) % 7, 'days')
                                : selectedDate;

                              props.func.setState({
                                startDate: finalDate.toDate(),
                                scheduledDate: finalDate.hour(8).minute(0).second(0).millisecond(0).toISOString(false)
                              });
                            }}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              fontSize: '14px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="0">{languageObj.sunday || "Sunday"}</option>
                            <option value="1">{languageObj.monday || "Monday"}</option>
                            <option value="2">{languageObj.tuesday || "Tuesday"}</option>
                            <option value="3">{languageObj.wednesday || "Wednesday"}</option>
                            <option value="4">{languageObj.thursday || "Thursday"}</option>
                            <option value="5">{languageObj.friday || "Friday"}</option>
                            <option value="6">{languageObj.saturday || "Saturday"}</option>
                          </select>
                        </div>
                      )}

                      <div className="MissionDetails-frequencyNote">
                        {props.func.state.frequency === "WEEKLY" && (
                          (languageObj.recurringWeeklyNote || "Recurring donations will be charged every {weekday} starting {date}")
                            .replace("{weekday}", props.func.state.startDate ? new Date(props.func.state.startDate).toLocaleDateString('en-US', { weekday: 'long' }) : new Date().toLocaleDateString('en-US', { weekday: 'long' }))
                            .replace("{date}", props.func.state.startDate ? new Date(props.func.state.startDate).toLocaleDateString() : new Date().toLocaleDateString())
                        )}
                        {props.func.state.frequency === "MONTHLY" && (
                          (languageObj.recurringDayNote || "Recurring donations will be charged on day {day} of each month")
                            .replace("{day}", props.func.state.startDate ? new Date(props.func.state.startDate).getDate() : new Date().getDate())
                        )}
                      </div>
                    </div>
                    {/* Duration Settings */}
                    <div className="MissionDetails-scheduleRow">
                      <div className="MissionDetails-scheduleLabel">
                        {languageObj.duration || "Duration"}
                      </div>
                      <div className="MissionDetails-checkbox-container">
                        <label className="MissionDetails-checkbox-label">
                          <input
                            type="checkbox"
                            checked={props.func.state.isInfiniteRecurring || false}
                            onChange={(e) => {
                              const isInfinite = e.target.checked;
                              let defaultOccurrences = 12;
                              if (props.func.state.frequency === "WEEKLY") {
                                defaultOccurrences = 52;
                              }
                              props.func.setState({
                                isInfiniteRecurring: isInfinite,
                                recurringOccurrences: isInfinite ? 0 : defaultOccurrences
                              });
                            }}
                            className="MissionDetails-checkbox"
                            style={{ accentColor: "#" + resolved.buttonColor }}
                          />
                          <span className="MissionDetails-checkboxText">
                            {languageObj.continueUntilCancel || "Continue until I cancel"}
                          </span>
                        </label>
                      </div>
                      {!props.func.state.isInfiniteRecurring && (
                        <div>
                          <div className="MissionDetails-headingText">
                            {props.func.state.frequency === "WEEKLY" && (languageObj.frequencyWeeks || "Number of weeks")}
                            {props.func.state.frequency === "MONTHLY" && (languageObj.frequency || "Number of months")}
                            <br />
                            {languageObj.example}
                          </div>
                          <div className="MissionDetails-inputContainer">
                            <input
                              min={MIN_RECURRING_OCCURRENCES}
                              step="1"
                              max={MAX_RECURRING_OCCURRENCES}
                              onChange={_handleOccurrences}
                              value={props.func.state.recurringOccurrences}
                              type="number"
                              className="MissionDetails-frequencyInput"
                              placeholder={props.func.state.frequency === "WEEKLY" ? "52" : "12"}
                              pattern="[0-9]*"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="MissionDetails-errorContainer">
              {renderErrorMsg(props.func.state.roErrMsg)}
            </div>


            <div
              className="MissionDetails-nextBtn"
              style={nextBtnStyle}
              onClick={props.func.onBtnClicked}
            >
              {languageObj.nextBtn}
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

function renderBar(
  targetAmount,
  currentAmount,
  progressStyle,
  progressBgStyle
) {
  if (targetAmount < 1 || currentAmount < 1) {
    return;
  }

  return (
    <div className="MissionDetails-progressContainer">
      <div className="MissionDetails-progressBg" style={progressBgStyle} />
      <div className="MissionDetails-progress" style={progressStyle}></div>
    </div>
  );
}

ViewMission.propTypes = {
  func: PropTypes.object.isRequired,
  logoStyle: PropTypes.object,
  isAuth: PropTypes.bool,
  language: PropTypes.string,
  color: PropTypes.object,
  buttonOverrideStyle: PropTypes.object,
  textOverrideGroupStyle: PropTypes.object,
  buttonOverrideGroupStyle: PropTypes.object,
  buttonOverrideTextStyle: PropTypes.object,
  backpath: PropTypes.string.isRequired,
};

export default ViewMission;